import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {Globals} from '../../globals';
import {AddressDataModel} from '../../models/person/address-data.model';
import {UserService} from '../../services/user.service';
import {PatientSearchModel} from '../../models/patient/patient-search.model';
import {PatientUpdateResponseModel} from '../../models/patient/patient-update-response.model';
import * as moment from 'moment';
import {PatientDoctorDataModel} from '../../models/patient/patient-doctor-data.model';
import {ContactDataModel} from '../../models/person/contact-data.model';
import {PatientDataModel} from '../../models/patient/patient-data.model';
import {tap} from 'rxjs/operators';
import {LocalStorageService} from '../../services/local-storage.service';
import {DoctorDataModel} from '../../models/doctors/doctor-data.model';
import {ToothSelectorResponseModel} from '../../dialog/tdo-tooth-selector-dialog/models/tooth-selector-response.model';
import {PatientSearchResponseModel} from '../../models/patient/patient-search-response.model';
import {dataContact, dataPatient} from '../ar/_models/ar.models';
import { FileManagerService } from '../file-manager/file-manager.service';

@Injectable({
    providedIn: 'root'
})
export class PatientService {
    recentPatients: PatientDataModel[] = [];

    constructor(private http: HttpClient, private globals: Globals, private localStorageService: LocalStorageService,
                private fileService: FileManagerService) {
    }

    getPatientNameAndAge() {
        if (!this.globals.ThePatient) {
            return null;
        }

        const patientBirthday = this.globals.ThePatient.BirthDate ? moment(this.globals.ThePatient.BirthDate) : null;
        let label = '';
        if (patientBirthday) {
            label = this.globals.currentPatientFullName + ' (' + moment().diff(patientBirthday, 'years') + ')';
        } else {
            label = this.globals.currentPatientFullName;
        }
        return label;
    }

    getPatients(): Observable<PatientDataModel[]> {
        let url: string;
        const method: string = 'Patient';

        url = environment.TDOurl + method + '/AllPatients';
        return this.http.get<PatientDataModel[]>(url);
    }

    getPatient(patientID: string): Observable<any> {
        let url: string;
        const method: string = 'Patient';

        if (patientID === undefined || patientID === null || patientID === ''){
            return new Observable();
        }


        url = environment.TDOurl + method + '/' + patientID;
        return this.http.get<any>(url)
            .pipe(
                tap((_) => {
                    this.saveRecentPatient(this.localStorageService.currentUserData.authdata.userid, patientID).subscribe(() => {
                        //
                    });
                })
            );
    }

    markPatientAsDeleted(PatientId: string) {
        return this.http.post<any>(`/api/Patient/markPatientAsDeleted/${PatientId}`, {});
    }

    advancedPatientSearch(patientSearchObj: any) {
        return this.http.post<any>('/api/patient/advanced-patient-search', patientSearchObj);
    }

    getTDO12Patient(patientNO: string): Observable<any> {
        let url: string;
        const method: string = 'Patient/TDO12Patient';
        url = environment.TDOurl + method + '/' + patientNO;
        return this.http.get<any>(url, UserService.authorizationHeaderObject);
    }

    setCurrentPatient(patientId: string) {
        localStorage.setItem('currentPatient', patientId);
        this.globals.currentPatientId = patientId;
    }

    getCurrentPatient() {
        if (this.globals.currentPatientId) {
            return this.globals.currentPatientId;
        } else if (localStorage.getItem('currentPatient')) {
            this.globals.currentPatientId = localStorage.getItem('currentPatient');
            return localStorage.getItem('currentPatient');
        } else {
            return null;
        }
    }

    clearCurrentPatient() {
        localStorage.removeItem('currentPatient');
        this.globals.currentPatientId = null;
    }

    createPatientAndSendCredentials(patientData: any) {
        let url: string;
        const method: string = 'Patient';
        url = environment.TDOurl + method + '/CreatePatientAndSendCredentials';
        return this.http.post<any>(url, patientData.ThePatient);
    }

    savePatient(patientData: any): Observable<PatientUpdateResponseModel> {
        let url: string;
        url = environment.TDOurl + 'Patient/CreateUpdatePatient';
        return this.http.post<PatientUpdateResponseModel>(url, patientData);
    }

    createPatientContact(patientId: string , contactData: dataContact): Observable<dataContact> {
        let url: string;
        url = environment.TDOurl + `Patient/createPatientContact/${patientId}`;
        return this.http.post<dataContact>(url, contactData);
    }



    UpdatePatientDNETA(patientData: dataPatient): Observable<dataPatient> {
        let url: string;
        url = environment.TDOurl + 'Patient/UpdatePatientDNETA';
        return this.http.post<dataPatient>(url, patientData);
    }



    getNewCommForPatientContact(ContactId: string): Observable<any> {
        return this.http.get<any>(`/api/Patient/getNewCommForPatientContact/${ContactId}`);
    }

    generateUsernameForPatient(PatientId: string): Observable<any> {
        return this.http.get<any>(`/api/Patient/generateUsernameForPatient/${PatientId}`);
    }

    generatePasswordForPatient(PatientId: string): Observable<any> {
        return this.http.get<any>(`/api/Patient/generatePasswordForPatient/${PatientId}`);
    }

    // changePasswordForPatient(ContactId: string): Observable<any> {
    //  const httpOptions = {
    //    headers: new HttpHeaders({ "Content-Type": "application/json" })
    //  };
    //  return this.http.get<any>(`/api/Patient/getNewCommForPatientContact/${ContactId}`, httpOptions);
    // }

    sendPatientUsernameAndPasswordToPatient(PatientId: string, host: string, username: string): Observable<any> {
        const payload = {
            host,
            username
        };

        return this.http.post<any>(`/api/Patient/sendPatientUsernameAndPasswordToPatient/${PatientId}`, payload);
    }

    getNewEmergencyContact(patientId: string): Observable<any> {
        return this.http.get<any>(`/api/Patient/getNewEmergencyContact/${patientId}`);
    }

    getNewComm(patientId: string): Observable<any> {
        return this.http.get<any>(`/api/Patient/getNewCommForPatient/${patientId}`);
    }

    getNewContactAddress(ContactId: string): Observable<AddressDataModel> {
        return this.http.get<AddressDataModel>(`/api/Patient/getNewContactAddress/${ContactId}`);
    }

    getNewCommForContact(ContactId: string): Observable<any> {
        return this.http.get<any>(`/api/Patient/getNewCommForContact/${ContactId}`);
    }

    saveDataPatient(patient: any): Observable<any> {
        return this.http.post<any>('/api/Patient/saveDataPatient', patient);
    }

    saveDataContact(patientId: string, contact: any): Observable<any> {
        return this.http.post<any>(`/api/Patient/${patientId}/saveDataContact`, contact);
    }

    saveContact(contact: any): Observable<any> {
        return this.http.post<any>('/api/Patient/saveContact', contact);
    }

    savePatientContact(dataPatientContact: any): Observable<any> {
        return this.http.post<any>('/api/Patient/savePatientContact', dataPatientContact);
    }

    saveContactStandalone(contact: ContactDataModel): Observable<ContactDataModel> {
        return this.http.post<ContactDataModel>('api/patient/save-contact-standalone', contact);
    }

    getNewContact(patientId): Observable<any> {
        return this.http.get<any>(`/api/Patient/getNewContact/${patientId}`);
    }

    getPatientContacts(patientId): Observable<any> {
        return this.http.get<any>(`/api/Patient/getPatientContacts/${patientId}`);
    }

    getNewPatient(): Observable<any> {
        return this.http.get<any>('/api/Patient/getNewPatient');
    }

    saveContactAddress(address: any): Observable<any> {
        return this.http.post<any>('/api/Patient/saveContactAddress', address);
    }

    saveContactMethod(contactMethod: any): Observable<any> {
        return this.http.post<any>('/api/Patient/saveContactMethod', contactMethod);
    }

    saveDataPatientReferralDoctor(dataPatientDoctor: PatientDoctorDataModel): Observable<PatientDoctorDataModel>{
        return this.http.post<PatientDoctorDataModel>('/api/Patient/saveDataPatientReferralDoctor', dataPatientDoctor);
    }

    getContactById(ContactId: string) {
        return this.http.get<any>(`/api/Patient/getContactById/${ContactId}`);
    }

    getPatientContactsTypeById(PatientId: string){
        return this.http.get<any>(`/api/Patient/getPatientsContactTypeById/${PatientId}`);
    }

    getRecentPatients(userId: string) {
        return this.http.get<PatientDataModel[]>(`api/patient/get-recent-patients/${userId}`)
            .pipe(
                tap((_) => {
                    _.forEach((p) => {
                        p.patientImageUrl = p.patientImageUrl ? this.fileService.getDocumentImageUrl(p.PatientId, p.patientImageUrl) : '/assets/Images/defaultPatient.PNG';
                    });

                    // Filter out deleted patients by checking dataContact.xauditDeleted
                    this.recentPatients = _.filter(p => 
                        // Check if dataContact exists and its xauditDeleted property is not true
                        !(p.dataContact && p.dataContact.xauditDeleted === true)
                    );
                })
            );
    }

    saveRecentPatient(userId: string, patientId: string) {
        return this.http.get<void>(`api/patient/save-recent-patient/${userId}/${patientId}`)
            .pipe(
                tap((_) => {
                    //this.getRecentPatients(userId).subscribe((res) => {});
                })
            );
    }

    patientSearch(searchFor: string) {
        const payload = {searchText: searchFor};
        return this.http.post<PatientSearchResponseModel>('api/patient/search', payload);
    }

    searchForPatient(searchFor: string): Observable<PatientSearchResponseModel> {

        const payload = {searchText: searchFor};
        return this.http.post<PatientSearchResponseModel>(
            '/api/patient/search-patients', payload, UserService.authorizationHeaderObject
        );
    }

    deletePatientDoctor(model: PatientDoctorDataModel) {
        return this.http.get<void>(`api/patient/delete-patient-doctor/${model.PatientId}/${model.DoctorSpecId}`);
    }

    deleteAddress(addressId: string) {
        return this.http.get<void>(`api/patient/delete-address/${addressId}`);
    }

    deleteCommunction(communicationId: string) {
        return this.http.get<void>(`api/patient/delete-communication/${communicationId}`);
    }

    handleError(error) {
        console.error('patient error: ', error.message);
    }

    deleteContact(contactId: string) {
        return this.http.get<void>(`api/patient/delete-contact/${contactId}`);
    }

    getPatientReferringDoctor(patientId: string) {
        return this.http.get<DoctorDataModel>(`api/patient/get-referring-doctor/${patientId}`);
    }
    getPatientAllReferringDoctor(patientId: string) {
        return this.http.get<any>(`api/patient/get-all-referring-doctors/${patientId}`);
    }

    getAllPatients(locid: string) {
        return this.http.get<PatientDataModel[]>(`api/patient/get-all-patients/${locid}`);
    }

    getAllPatientCasesAndTeeth(patientId: string) {
        return this.http.get<ToothSelectorResponseModel[]>(`api/patient-case/get-patient-case-teeth/${patientId}`);
    }
}
